import React from "react";
import styled from "styled-components";

const MismatchHighlight = styled.span`
  color: #dc3545;
  font-weight: 600;
`;

const QuantityMismatch = styled.span`
  color: #dc3545;
  font-weight: 600;
`;

/**
 * Parse a case signature string ("CPT:qty,CPT:qty,...") into a map of CPT code -> quantity.
 */
export function parseCaseSignature(signature: string): Map<string, number> {
  const result = new Map<string, number>();
  if (!signature) return result;

  signature.split(",").forEach((pair) => {
    const [cpt, qty] = pair.split(":");
    if (cpt && qty) {
      result.set(cpt, parseInt(qty, 10));
    }
  });

  return result;
}

/**
 * Group CPT code pairs into rows based on total count
 * - 1-3 codes: single row
 * - 4 codes: 2+2
 * - 5 codes: 3+2
 * - 6+: groups of 3
 */
function groupIntoCptRows<T>(items: T[]): T[][] {
  const total = items.length;
  if (total <= 3) return [items];
  if (total === 4) return [items.slice(0, 2), items.slice(2)];
  if (total === 5) return [items.slice(0, 3), items.slice(3)];

  // 6+: groups of 3
  const rows: T[][] = [];
  for (let i = 0; i < total; i += 3) {
    rows.push(items.slice(i, i + 3));
  }
  return rows;
}

/**
 * Render a case signature with the differences vs another signature highlighted in red:
 * - a CPT code absent from the other signature → the whole code:qty pair is highlighted
 * - a CPT code present but with a different quantity → only the quantity is highlighted
 *
 * Shared by the Estimate Comparison tab and the 837-vs-835 row in the claim modal's
 * Financial Summary (CLE-2266) so the diff styling stays consistent in both places.
 */
export function renderCaseSignatureWithHighlights(
  signature: string,
  otherSignature: string
): React.ReactNode {
  if (!signature) return "-";

  const otherMap = parseCaseSignature(otherSignature);
  const pairs = signature.split(",");
  const needsHighlighting = otherSignature && signature !== otherSignature;

  // Render a single CPT pair with optional highlighting
  const renderPair = (pair: string, isLastInRow: boolean) => {
    const [cpt, qty] = pair.split(":");
    const otherQty = otherMap.get(cpt);
    const separator = isLastInRow ? "" : ", ";

    if (needsHighlighting && otherQty === undefined) {
      return (
        <React.Fragment key={cpt}>
          <MismatchHighlight>{pair}</MismatchHighlight>
          {separator}
        </React.Fragment>
      );
    }

    if (needsHighlighting && otherQty !== parseInt(qty, 10)) {
      return (
        <React.Fragment key={cpt}>
          {cpt}:<QuantityMismatch>{qty}</QuantityMismatch>
          {separator}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={cpt}>
        {pair}
        {separator}
      </React.Fragment>
    );
  };

  const rows = groupIntoCptRows(pairs);

  // If only one row, render inline without wrapping divs
  if (rows.length === 1) {
    return rows[0].map((pair, idx) =>
      renderPair(pair, idx === rows[0].length - 1)
    );
  }

  // Multiple rows: wrap each in a div with nowrap to prevent mid-row breaks
  return rows.map((row, rowIdx) => (
    <div key={rowIdx} style={{ whiteSpace: "nowrap" }}>
      {row.map((pair, idx) => renderPair(pair, idx === row.length - 1))}
    </div>
  ));
}
